import { useEffect, useState, useCallback, useRef } from "react";
import { getDomains } from "../services/domainsService";

interface IuseFetchDomains {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  fetchOnMount?: boolean; // Controls whether to fetch data immediately on mount
}

const useFetchDomains = ({
  setIsLoading,
  fetchOnMount = true, // Default to true for backward compatibility
}: IuseFetchDomains) => {
  const [domainsData, setDomainsData] = useState<any[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false); // Internal loading state for Autocomplete

  // Use refs for guards so the callback identity doesn't depend on them,
  // preventing useEffect re-triggers when hasFetched or setIsLoading change.
  const hasFetchedRef = useRef<boolean>(false);
  const isFetchingRef = useRef<boolean>(false);
  const setIsLoadingRef = useRef(setIsLoading);
  setIsLoadingRef.current = setIsLoading;

  const fetchDomains = useCallback(async () => {
    // Prevent duplicate fetches AND concurrent in-flight requests
    if (hasFetchedRef.current || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsFetching(true);
    setIsLoadingRef.current(true);
    try {
      const result = await getDomains();
      const res = result && Array.isArray(result) ? result.reverse() : [];
      setDomainsData(res);
      hasFetchedRef.current = true;
    } catch (err) {
      console.error("Error fetching domains:", err);
      setDomainsData([]);
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
      setIsLoadingRef.current(false);
    }
  }, []); // No deps — uses refs for all mutable values

  useEffect(() => {
    if (fetchOnMount) {
      fetchDomains();
    }
  }, [fetchOnMount, fetchDomains]);

  return [domainsData, fetchDomains, isFetching] as const;
};

export default useFetchDomains;
